/**
 * Phase 5.4a-fix (2026-05-15) — Global axios offline interceptor.
 *
 * Plugs into the auth-bound axios instance. Does TWO things:
 *
 * 1) On REQUEST: adds an `Idempotency-Key: <uuid>` header to every
 *    POST / PATCH / PUT / DELETE call (unless already provided or the URL
 *    is in the blacklist). This guarantees backend-side de-dup on retry.
 *
 * 2) On RESPONSE ERROR: when the failure is a *network* error (no HTTP
 *    response — DNS, timeout, offline, etc.) AND the method is a write
 *    AND the URL is not blacklisted → enqueue the action in IndexedDB
 *    outbox and RESOLVE the promise with a synthetic 202 response so the
 *    calling component thinks it succeeded.
 *
 * The synthetic body is built to be permissive: it echoes the request
 * body + adds `__offline: true`, `idempotency_key`, and a best-effort
 * `articles` array (for `/stock/bulk-add`-style endpoints) so the
 * existing UI keeps working without modification.
 *
 * Online HTTP errors (4xx / 5xx with a response) are passed through
 * unchanged — we don't silently swallow business errors.
 */
import syncEngine, { uuidv4 } from './syncEngine';
import { addOutboxAction } from './offlineDB';

const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

// Paths that MUST NEVER be queued offline (auth, hardware, file uploads).
const OFFLINE_BLACKLIST = [
  /^\/auth\//i,
  /^\/push\/(subscribe|unsubscribe|test|preferences)/i,
  /^\/caisse\/(ouvrir|fermer|reouvrir)/i,
  /^\/upload(s)?\//i,
  /\/upload$/i,
  /\/stock\/catalog\/[^/]+\/photo$/i,
  /^\/printer\//i,            // Local printer / Bixolon endpoints
  /^\/admin\/devis\/.*\/send$/i,  // Email sending
];

const isBlacklisted = (url) => {
  if (!url) return true;
  const clean = url.split('?')[0];
  return OFFLINE_BLACKLIST.some((rx) => rx.test(clean));
};

// Best-effort synthetic body for common create endpoints. Pages that
// expect richer shapes can detect `__offline` and adapt.
const buildSyntheticBody = ({ method, url, body, idempotency_key, queued_id }) => {
  const base = { __offline: true, idempotency_key, queued_id, status: 'queued' };

  // `/stock/bulk-add` returns { created, updated, errors, articles[] }.
  if (/\/stock\/bulk-add$/.test(url || '')) {
    const items = (body && body.items) || [];
    return {
      ...base,
      created: items.length,
      updated: 0,
      errors: [],
      articles: items.map((it) => ({
        id: `offline-${uuidv4()}`,
        reference: it.reference,
        designation: it.designation,
        ean: it.ean,
        prix_achat: it.prix_achat,
        prix_vente: it.prix_vente,
        added_qty: it.quantite_add,
        location: it.location,
        categorie: it.categorie,
        fournisseur: it.fournisseur,
        __offline: true,
      })),
    };
  }

  // `/stock` (single create) returns the StockArticleResponse shape.
  if (url === '/stock' && method === 'post') {
    return { ...base, id: `offline-${uuidv4()}`, ...(body || {}) };
  }

  // `/devices` (repair creation) returns the device object.
  if (url === '/devices' && method === 'post') {
    return {
      ...base,
      id: `offline-${uuidv4()}`,
      ref: body?.ref || `OFFLINE-${idempotency_key.slice(0, 6).toUpperCase()}`,
      ...(body || {}),
    };
  }

  // `/clients` create.
  if (url === '/clients' && method === 'post') {
    return { ...base, id: `offline-${uuidv4()}`, ...(body || {}) };
  }

  // `/admin/devis` create.
  if (url === '/admin/devis' && method === 'post') {
    return { ...base, id: `offline-${uuidv4()}`, ref: `OFFLINE-DEVIS-${idempotency_key.slice(0, 6).toUpperCase()}`, ...(body || {}) };
  }

  // Generic fallback: echo the body so any field reads succeed.
  return { ...base, ...(body || {}) };
};

const isNetworkError = (err) => {
  if (err?.response) return false;
  const code = err?.code;
  const msg = err?.message || '';
  return (
    code === 'ERR_NETWORK' ||
    code === 'ECONNABORTED' ||
    code === 'ETIMEDOUT' ||
    msg === 'Network Error' ||
    /failed to fetch/i.test(msg)
  );
};

/**
 * Attach the interceptor to an axios instance. Returns an `eject` fn the
 * caller can use to dispose. Safe to call multiple times — we tag the
 * instance so we don't double-bind.
 */
export const installOfflineInterceptor = (instance) => {
  if (!instance || instance.__remobilesOfflineBound) return () => {};
  instance.__remobilesOfflineBound = true;

  // 1) Request → add Idempotency-Key on writes.
  const reqId = instance.interceptors.request.use((config) => {
    const method = (config.method || 'get').toLowerCase();
    const url = config.url || '';
    if (WRITE_METHODS.has(method) && !isBlacklisted(url)) {
      config.headers = config.headers || {};
      if (!config.headers['Idempotency-Key'] && !config.headers['idempotency-key']) {
        config.headers['Idempotency-Key'] = uuidv4();
      }
    }
    return config;
  });

  // 2) Response error → queue on network failure.
  const resId = instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const cfg = error?.config || {};
      const method = (cfg.method || 'get').toLowerCase();
      const url = cfg.url || '';
      if (!WRITE_METHODS.has(method) || !isNetworkError(error) || isBlacklisted(url)) {
        return Promise.reject(error);
      }
      // Parse the body (axios already serializes on send → cfg.data may be string).
      let body = cfg.data;
      if (typeof body === 'string') {
        try { body = JSON.parse(body); } catch (_) { /* keep raw */ }
      }
      const key = cfg.headers?.['Idempotency-Key'] || cfg.headers?.['idempotency-key'] || uuidv4();
      try {
        const doc = await addOutboxAction({
          idempotency_key: key,
          method: method.toUpperCase(),
          url,
          body: body || null,
        });
        const synthetic = buildSyntheticBody({
          method, url, body, idempotency_key: key, queued_id: doc.id,
        });
        // Refresh sync engine state so the banner reflects the new pending.
        try { await syncEngine.flush(); } catch (_) { /* offline → noop */ }
        return {
          data: synthetic,
          status: 202,
          statusText: 'Queued (offline)',
          headers: {},
          config: cfg,
          __offline: true,
        };
      } catch (_) {
        // IndexedDB unavailable — surface the original error.
        return Promise.reject(error);
      }
    },
  );

  return () => {
    instance.interceptors.request.eject(reqId);
    instance.interceptors.response.eject(resId);
  };
};

export default installOfflineInterceptor;
